// Residents: units, rooms and tenants. A condo has units, a rental unit has
// rooms, a room holds tenants. A studio or an owner-occupied unit has no rooms
// and its tenants live in the whole unit.

export const UNIT_KINDS = ['rental', 'studio', 'owner'];

const NAME_MAX_UNIT = 60;
const NAME_MAX_TENANT = 120;
const PHONE_MIN = 6;
const PHONE_MAX = 32;
const LANGUAGE_MAX = 20;

// Rooms exist only on a rental unit.
export const hasRooms = kind => kind === 'rental';

export function emptyUnit() {
  return { condo_id: '', name: '', kind: 'rental', active: true, rooms: '' };
}

export function unitToForm(unit) {
  return {
    condo_id: unit.condo_id,
    name: unit.name,
    kind: unit.kind,
    active: !!unit.active,
    rooms: '',
  };
}

// "Room 1, Room 2" -> ['Room 1', 'Room 2'], blanks and repeats dropped.
export function parseRoomNames(text) {
  const seen = new Set();
  return String(text || '')
    .split(/[,\n]/)
    .map(name => name.trim())
    .filter(name => {
      const key = name.toLowerCase();
      if (!name || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function validateUnit(form, { isEdit }) {
  const errors = {};
  if (!isEdit && !form.condo_id) errors.condo_id = 'CONDO';
  const name = form.name.trim();
  if (!name || name.length > NAME_MAX_UNIT) errors.name = 'UNIT_NAME';
  if (parseRoomNames(form.rooms).some(room => room.length > NAME_MAX_UNIT)) {
    errors.rooms = 'ROOM_NAME';
  }
  return errors;
}

// Create sends the rooms (rental only); an update sends name, kind and active.
export function unitPayload(form, { isEdit }) {
  const body = {
    name: form.name.trim(),
    kind: form.kind,
    active: form.active,
  };
  if (isEdit) return body;
  const create = { condo_id: form.condo_id, ...body };
  if (hasRooms(form.kind)) create.rooms = parseRoomNames(form.rooms);
  return create;
}

export function validateRoomName(name) {
  const trimmed = String(name || '').trim();
  return !trimmed || trimmed.length > NAME_MAX_UNIT ? 'ROOM_NAME' : '';
}

export function emptyTenant() {
  return {
    condo_id: '',
    unit_id: '',
    room_id: '',
    name: '',
    phone: '',
    language: '',
    active: true,
  };
}

export function tenantToForm(tenant) {
  return {
    condo_id: tenant.condo_id,
    unit_id: tenant.unit_id,
    room_id: tenant.room_id || '',
    name: tenant.name,
    phone: tenant.phone,
    language: tenant.language || '',
    active: !!tenant.active,
  };
}

// `unit` is the selected unit (to know if it needs a room), or undefined.
export function validateTenant(form, unit) {
  const errors = {};
  if (!form.condo_id) errors.condo_id = 'CONDO';
  if (!form.unit_id) errors.unit_id = 'UNIT';
  if (unit && hasRooms(unit.kind) && !form.room_id) errors.room_id = 'ROOM';
  const name = form.name.trim();
  if (!name || name.length > NAME_MAX_TENANT) errors.name = 'TENANT_NAME';
  const phone = form.phone.trim();
  if (phone.length < PHONE_MIN || phone.length > PHONE_MAX)
    errors.phone = 'PHONE';
  if (form.language.length > LANGUAGE_MAX) errors.language = 'LANGUAGE';
  return errors;
}

// A room is sent only for a rental unit; never for a studio or owner unit.
export function tenantPayload(form, unit) {
  const body = {
    unit_id: form.unit_id,
    name: form.name.trim(),
    phone: form.phone.trim(),
    language: form.language,
    active: form.active,
  };
  if (unit && hasRooms(unit.kind)) body.room_id = form.room_id;
  return body;
}

// The Languages card edits `languages` as comma-separated text.
export function parseLanguages(text) {
  const seen = new Set();
  return String(text || '')
    .split(',')
    .map(language => language.trim())
    .filter(language => {
      const key = language.toLowerCase();
      if (!language || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}
